import { DISPLAY_NAME } from './config';

// Deliberately short list of unambiguous words. Whole-token match after leet-speak normalisation.
const BLOCKED = [
  'anal',
  'anus',
  'arse',
  'ass',
  'asshole',
  'bastard',
  'bitch',
  'bollocks',
  'boob',
  'boobs',
  'bugger',
  'cock',
  'crap',
  'cum',
  'cunt',
  'dick',
  'dildo',
  'fag',
  'faggot',
  'fuck',
  'fucker',
  'fucking',
  'jizz',
  'knob',
  'nazi',
  'nigga',
  'nigger',
  'paki',
  'penis',
  'piss',
  'prick',
  'pussy',
  'retard',
  'shit',
  'slag',
  'slut',
  'spastic',
  'tits',
  'twat',
  'wank',
  'wanker',
  'whore',
];
// Substring match for the worst of them, to catch "xxfuckxx".
const BLOCKED_SUBSTRING = ['fuck', 'nigg', 'faggot', 'wanker'];

const LEET: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '@': 'a',
  $: 's',
};

function normalise(s: string): string {
  return s
    .toLowerCase()
    .split('')
    .map((ch) => LEET[ch] ?? ch)
    .join('');
}

export function containsProfanity(name: string): boolean {
  const n = normalise(name);
  const compact = n.replace(/[^a-z]/g, '');
  if (BLOCKED_SUBSTRING.some((w) => compact.includes(w))) return true;
  const tokens = n.split(/[^a-z]+/).filter(Boolean);
  return tokens.some((t) => BLOCKED.includes(t));
}

export type NameValidation = { ok: true; name: string } | { ok: false; reason: string };

/**
 * Display-name rules (PRD §7): 3–16 characters, letters/numbers/spaces, profanity-filtered.
 * Whitespace is trimmed and collapsed before checking.
 */
export function validateDisplayName(raw: string): NameValidation {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length < DISPLAY_NAME.min) {
    return { ok: false, reason: `Name must be at least ${DISPLAY_NAME.min} characters` };
  }
  if (name.length > DISPLAY_NAME.max) {
    return { ok: false, reason: `Name must be at most ${DISPLAY_NAME.max} characters` };
  }
  if (!/^[A-Za-z0-9 ]+$/.test(name)) {
    return { ok: false, reason: 'Use letters, numbers and spaces only' };
  }
  if (containsProfanity(name)) {
    return { ok: false, reason: 'That name is not allowed' };
  }
  return { ok: true, name };
}

/** "Fan 4821" — default name for a new anonymous player. */
export function randomGuestName(random: () => number = Math.random): string {
  const n = Math.floor(random() * 9000) + 1000;
  return `Fan ${n}`;
}

/**
 * Name with a numeric suffix so it fits the max length: "Alex" → "Alex2".
 * Used when a chosen name is already taken.
 */
export function withSuffix(name: string, suffix: number): string {
  const s = String(suffix);
  const base = name.slice(0, DISPLAY_NAME.max - s.length).trimEnd();
  return `${base}${s}`;
}
